import { Cron } from "croner";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const NAMES: Record<string, number> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
const NUM = /^\d+$/;

const at = (minute: string, hour: string) => `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
const day = (d: string): number | null => {
  const n = NUM.test(d) ? Number(d) : (NAMES[d.toLowerCase()] ?? NaN);
  return n >= 0 && n <= 7 ? n % 7 : null;
};
const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "th" : (["th", "st", "nd", "rd"][n % 10] ?? "th")}`;
const list = (items: string[]) => (items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);

/**
 * A five-part cron expression in plain words, for the shapes people write ("Weekdays at 09:00", "Every 15 minutes",
 * "Monthly on the 1st at 08:30"); null for anything else — seconds, ranges of hours, a day of the month together with a
 * weekday (which croner reads as either) — where the next times shown beside it say what it does.
 */
export function cronInWords(expression: string): string | null {
  const parts = expression.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minute, hour, dom, month, dow] = parts as [string, string, string, string, string];
  if (month !== "*") return null;
  const every = /^\*\/(\d+)$/;
  if (hour === "*" && dom === "*" && dow === "*") {
    if (minute === "*") return "Every minute";
    const m = minute.match(every);
    if (m) return Number(m[1]) === 1 ? "Every minute" : `Every ${m[1]} minutes`;
    if (NUM.test(minute) && Number(minute) < 60) return Number(minute) === 0 ? "Every hour, on the hour" : `Every hour at :${minute.padStart(2, "0")}`;
    return null;
  }
  if (!NUM.test(minute) || Number(minute) > 59) return null;
  const h = hour.match(every);
  if (h && dom === "*" && dow === "*") return `Every ${h[1]} hours, at :${minute.padStart(2, "0")} past`;
  if (!NUM.test(hour) || Number(hour) > 23) return null;
  const time = at(minute, hour);
  if (dom === "*" && dow === "*") return `Every day at ${time}`;
  if (dom !== "*" && dow !== "*") return null;
  if (dom !== "*") return NUM.test(dom) && Number(dom) >= 1 && Number(dom) <= 31 ? `Monthly on the ${ordinal(Number(dom))} at ${time}` : null;
  const range = dow.match(/^(\w+)-(\w+)$/);
  if (range) {
    const [from, to] = [day(range[1]!), day(range[2]!)];
    if (from === 1 && to === 5) return `Weekdays at ${time}`;
    if (from === 6 && (to === 0 || range[2] === "7")) return `Weekends at ${time}`;
    return null;
  }
  const days = dow.split(",").map(day);
  if (days.some((d) => d === null)) return null;
  const set = [...new Set(days as number[])].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
  if (set.length === 2 && set.includes(0) && set.includes(6)) return `Weekends at ${time}`;
  return `Every ${list(set.map((d) => DAYS[d]!))} at ${time}`;
}

/**
 * What a cron schedule will do, the way the server reads it (croner, the same options): its words, the next `count`
 * times written in the schedule's own timezone (the server's, UTC, when it has none), or why it cannot run.
 */
export function cronPreview(expression: string, timezone: string | null, count = 3, from = new Date()): { words: string | null; next: string[]; zone: string } | { error: string } {
  const zone = timezone?.trim() || "UTC";
  let format: Intl.DateTimeFormat;
  try {
    format = new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: zone });
  } catch {
    return { error: `${zone} is not a timezone (use a name like Europe/Athens)` };
  }
  try {
    const next = new Cron(expression.trim(), { timezone: zone, paused: true }).nextRuns(count, from);
    if (next.length === 0) return { error: "it never fires" };
    return { words: cronInWords(expression), next: next.map((d) => format.format(d)), zone };
  } catch (err) {
    return { error: (err as Error).message };
  }
}
